import { useState } from 'react';
import { CalendarIcon } from '@heroicons/react/outline';
import cn from 'classnames';

import { Skeleton } from 'components/Skeleton';

import { dateLabelOf, initialsOf, summaryOf, timingOf } from '../nextAppointment';
import { useNextAppointment } from '../useNextAppointment';
import { BookingDetailsDrawer } from './BookingDetailsDrawer';
import styles from './NextAppointment.module.css';

type Props = {
  /**
   * The customer whose diary this is — their email, because that is what the
   * booking system holds to join on; it knows nothing of a Taper customer id.
   */
  customerEmail?: string | null;
  /** Or the provider whose diary this is, by the booking system's own id. */
  providerId?: string | null;
  className?: string;
};

/** The box every state is drawn in, so none of them changes the header's height. */
const Strip = ({ children, className, label }: { children: React.ReactNode; className?: string; label?: string }) => (
  <div
    aria-label={label}
    className={cn(styles.strip, 'flex h-16 min-w-0 items-center gap-3 rounded-md border border-stroke-subtle bg-surface-muted px-3', className)}
  >
    {children}
  </div>
);

/**
 * What this customer is in for next, beside the buttons that would book them
 * another one.
 *
 * Read from the booking system rather than Taper's own table, so an appointment
 * booked anywhere counts. Renders nothing at all when the tenant has no booking
 * or the customer has no email to match on — an empty box saying "no
 * appointments" would be claiming something we cannot know.
 */
export const NextAppointment = ({ customerEmail, providerId, className }: Props) => {
  const { booking, loading, now, isEnabled } = useNextAppointment({ customerEmail, providerId });
  const [isOpen, setIsOpen] = useState(false);

  if (!isEnabled) return null;

  if (loading) {
    return (
      <Strip className={className}>
        <Skeleton className="w-full" />
      </Strip>
    );
  }

  if (!booking) {
    return (
      <Strip className={className} label="Next appointment">
        <CalendarIcon className="size-4 shrink-0 text-content-secondary" />
        <span className="truncate text-sm text-content-muted">No upcoming appointments</span>
      </Strip>
    );
  }

  const timing = timingOf(booking, now);

  return (
    <>
      <Strip className={className} label="Next appointment">
        <CalendarIcon className="size-4 shrink-0 text-content-secondary" />
        {/* Day over time: which day it is settles whether the time matters at
            all, so it is read first. */}
        <div className="shrink-0 leading-tight">
          <p className="text-sm font-semibold text-content-primary">{dateLabelOf(booking, now)}</p>
          <p className="text-sm tabular-nums text-content-secondary">
            {booking.startTime}
            <span className="text-content-muted"> – {booking.endTime}</span>
          </p>
        </div>

        {timing.kind === 'soon' && <span className="shrink-0 text-xs font-semibold text-status-warning-fg">{timing.label}</span>}
        {timing.kind === 'now' && (
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-status-success-fg">
            <span className="size-1.5 rounded-full bg-status-success-solid" aria-hidden="true" />
            {timing.label}
          </span>
        )}

        <span className="h-10 w-px shrink-0 bg-surface-hover" aria-hidden="true" />

        <div className={cn(styles.who, 'flex min-w-0 flex-1 items-center gap-2')}>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-inverse text-[10px] font-bold text-content-inverse">
            {initialsOf(booking.providerName)}
          </span>
          {/* One line, now that the line under it is free. Never truncated:
              whose appointment it is, is the one thing here that cannot be
              guessed from the rest — what it is for gives way first, and takes
              the room that leaves. */}
          <div className="flex min-w-0 items-baseline gap-2">
            <p className="shrink-0 whitespace-nowrap text-sm font-semibold leading-5 text-content-primary">{booking.providerName}</p>
            <p className={cn(styles.detail, 'truncate text-xs leading-5 text-content-muted')}>{summaryOf(booking, now)}</p>
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="h-7 rounded border border-stroke-default bg-surface-card px-2.5 text-[13px] font-semibold text-content-primary hover:bg-surface-muted"
          >
            Open
          </button>
        </div>
      </Strip>

      {/* The same drawer the Bookings page opens, so an appointment reads the
          same wherever it is looked at. Rescheduling and cancelling live in
          there, which is also where the provider's real openings are. */}
      <BookingDetailsDrawer booking={isOpen ? booking : null} onClose={() => setIsOpen(false)} />
    </>
  );
};
